import { cn } from "@/lib/utils";
import { ui } from "@patient-watch/ui";
import { Filter, Loader2, X } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Admin filter dialogs, styled after the analytics filter dialog: one column of labelled
 * full-width controls, a scrolling body, and a fixed footer. Every control is 48px tall.
 */
export const adminFilterControlClass = "w-full min-h-12";

/** Labelled filter control, matching analytics FacetField. */
export function AdminFilterField({
    label,
    annotation,
    htmlFor,
    children,
}: {
    label: string;
    /** Short note under the label, for filters that are not obvious from the name. */
    annotation?: string;
    htmlFor?: string;
    children: ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <div className="px-0.5">
                <label htmlFor={htmlFor} className="block font-medium text-2xs text-slate-500">
                    {label}
                </label>
                {annotation ? <p className="text-2xs text-slate-500 leading-snug">{annotation}</p> : null}
            </div>
            {children}
        </div>
    );
}

export function AdminFilterSelect<T extends string>({
    id,
    value,
    onValueChange,
    options,
}: {
    id?: string;
    value: T;
    onValueChange: (value: T) => void;
    options: ReadonlyArray<{ value: T; label: ReactNode }>;
}) {
    return (
        <ui.Select value={value} onValueChange={(next) => onValueChange(next as T)}>
            <ui.SelectTrigger id={id} className={adminFilterControlClass}>
                <ui.SelectValue />
            </ui.SelectTrigger>
            <ui.SelectContent className="max-h-[min(70vh,28rem)]">
                {options.map((option) => (
                    <ui.SelectItem key={option.value} value={option.value}>
                        {option.label}
                    </ui.SelectItem>
                ))}
            </ui.SelectContent>
        </ui.Select>
    );
}

export function AdminFilterMultiSelect({
    options,
    value,
    onChange,
    placeholder,
}: {
    options: { value: string; label: string }[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder: string;
}) {
    return (
        <ui.MultiSelect
            options={options}
            value={value}
            onValueChange={onChange}
            placeholder={placeholder}
            className={adminFilterControlClass}
        />
    );
}

/** Free-text UUID filter (for ids with no name to search by). */
export function AdminFilterIdInput({
    id,
    value,
    onChange,
    placeholder,
    invalid = false,
}: {
    id?: string;
    value: string | null;
    onChange: (value: string | null) => void;
    placeholder: string;
    invalid?: boolean;
}) {
    return (
        <div className="space-y-1">
            <ui.Input
                id={id}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value.trim() || null)}
                placeholder={placeholder}
                aria-invalid={invalid}
                className="h-12"
            />
            {invalid ? <p className="text-red-700 text-xs">Enter a full UUID.</p> : null}
        </div>
    );
}

interface AdminFiltersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    activeCount: number;
    onClear: () => void;
    onApply: () => void;
    isApplying?: boolean;
    applyDisabled?: boolean;
    children: ReactNode;
}

/** Filters button plus dialog. Shared by every admin table toolbar. */
export function AdminFiltersDialog({
    open,
    onOpenChange,
    title,
    description,
    activeCount,
    onClear,
    onApply,
    isApplying = false,
    applyDisabled = false,
    children,
}: AdminFiltersDialogProps) {
    return (
        <ui.Dialog open={open} onOpenChange={onOpenChange}>
            <ui.DialogTrigger asChild>
                <ui.Button variant="outline" size="sm" type="button" className="gap-1.5 h-12 min-h-12 shrink-0">
                    <Filter className="w-3.5 h-3.5" />
                    Filters
                    {activeCount > 0 ? (
                        <span className="flex justify-center items-center bg-sky-100 dark:bg-sky-900 px-1.5 rounded-full min-w-5 h-5 font-medium text-sky-800 dark:text-sky-100 text-xs tabular-nums leading-none">
                            {activeCount}
                        </span>
                    ) : null}
                </ui.Button>
            </ui.DialogTrigger>
            <ui.DialogContent className="flex flex-col gap-0 p-0 w-full max-w-[min(96vw,36rem)] sm:max-w-xl min-h-0 max-h-[min(92svh,44rem)] overflow-hidden">
                <ui.DialogHeader className="px-6 pt-6 pr-14 pb-3 shrink-0">
                    <ui.DialogTitle>{title}</ui.DialogTitle>
                    <ui.DialogDescription>{description}</ui.DialogDescription>
                </ui.DialogHeader>
                <div className="flex-1 space-y-4 px-6 py-2 min-h-0 overflow-y-auto">{children}</div>
                <ui.DialogFooter className="sm:justify-end gap-2 bg-white dark:bg-slate-950 px-6 py-4 border-t shrink-0">
                    <ui.Button type="button" size="md" variant="outline" onClick={onClear}>
                        Clear filters
                    </ui.Button>
                    <ui.Button type="button" size="md" disabled={isApplying || applyDisabled} onClick={onApply}>
                        {isApplying ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : null}
                        Save
                    </ui.Button>
                </ui.DialogFooter>
            </ui.DialogContent>
        </ui.Dialog>
    );
}

export interface AdminActiveFilterChip {
    key: string;
    label: ReactNode;
    onRemove: () => void;
}

/** "Filtered by" row under a toolbar, like the analytics view summary, with removable chips. */
export function AdminActiveFilters({ chips, onClearAll }: { chips: AdminActiveFilterChip[]; onClearAll: () => void }) {
    if (chips.length === 0) return null;
    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-500 text-xs uppercase tracking-wide">Filtered by</span>
            {chips.map((chip) => (
                <ui.Badge key={chip.key} variant="outline" className="gap-1 pr-1 max-w-full">
                    <span className="truncate">{chip.label}</span>
                    <button
                        type="button"
                        onClick={chip.onRemove}
                        className="hover:bg-muted p-0.5 rounded-full text-muted-foreground hover:text-foreground shrink-0"
                        aria-label="Remove filter"
                    >
                        <X className="size-3" />
                    </button>
                </ui.Badge>
            ))}
            <button
                type="button"
                onClick={onClearAll}
                className={cn("text-sky-700 dark:text-sky-300 text-xs underline-offset-2 hover:underline")}
            >
                Clear all
            </button>
        </div>
    );
}
